import { defineStore } from "pinia";
import api from "@/services/api";
import { applyPwaSettings } from "@/utils/pwaHelper";
import { updateSchoolConfig } from "@/services/schoolService";

function getThemeColor(configs, colorKey) {
    if (!configs) return null;
    if (typeof configs === 'object' && !Array.isArray(configs)) {
        if (configs[colorKey]) return configs[colorKey];
        if (configs.configs && configs.configs[colorKey]) return configs.configs[colorKey];
    }
    if (Array.isArray(configs)) {
        const item = configs.find(c => c.key === colorKey || c.name === colorKey || c.slug === colorKey);
        if (item) return item.value || item.val || item.content;
    }
    return null;
}

export const useConfigStore = defineStore("config", {
    state: () => ({
        configs: [],
        disabledModules: [],
        loading: false,
        fetchedAt: 0,
        ttl: 1 * 60_000
    }),

    persist: true,

    actions: {
        applyThemeColors() {
            const primary = getThemeColor(this.configs, 'primary_color');
            const secondary = getThemeColor(this.configs, 'secondary_color');
            if (primary) {
                document.documentElement.style.setProperty('--primary-color', primary);
                try {
                    localStorage.setItem('cached_primary_color', primary);
                } catch (err) {
                    console.warn('Failed to cache primary color:', err);
                }
            }
            if (secondary) {
                document.documentElement.style.setProperty('--secondary-color', secondary);
                try {
                    localStorage.setItem('cached_secondary_color', secondary);
                } catch (err) {
                    console.warn('Failed to cache secondary color:', err);
                }
            }
        },

        async fetchConfig() {
            // Apply current stored colors immediately
            this.applyThemeColors();

            try {
                const res = await api.get('/config');
                if (res.data && res.data.data) {
                    this.configs = res.data.data;
                    this.disabledModules = res.data.data?.disabled_modules || [];
                    this.fetchedAt = Date.now();
                    
                    // Update theme colors immediately when fresh config is received
                    this.applyThemeColors();

                    const rawConfig = res.data.data;
                    const schoolLogo = rawConfig.school_logo || rawConfig.logo || rawConfig.app_logo || rawConfig.logo_url || rawConfig.school_info?.logo || rawConfig.pwa?.pwa_icon;
                    const schoolName = rawConfig.school_name || rawConfig.name || rawConfig.school_info?.name || rawConfig.pwa?.pwa_name;
                    if (schoolLogo || schoolName) {
                        updateSchoolConfig({
                            ...(schoolLogo ? { logo: schoolLogo } : {}),
                            ...(schoolName ? { name: schoolName } : {})
                        });
                    }

                    if (res.data.data?.pwa) {
                        applyPwaSettings(res.data.data.pwa);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch config:", err);
                this.applyThemeColors();
            }
        },
        isDisabled(slugOrModule) {
            if (!this.disabledModules || !Array.isArray(this.disabledModules)) return false;
            if (Array.isArray(slugOrModule)) {
                return slugOrModule.length > 0 && slugOrModule.some(s => this.disabledModules.includes(s));
            }
            return this.disabledModules.includes(slugOrModule);
        },
        reset() {
            this.configs = [];
            this.disabledModules = [];
        }
    },
});